'use client';

import { FEE_INCLUSIVE_CONTENT_CARDS_DEFAULT, FEE_INCLUSIVE_CONTENT_PDP_DEFAULT } from '@/constants/inventory';
import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import {
  getAdvertisedPriceStatus,
  getListPriceLabel,
  hasActiveRebate,
  isAdvertisedPriceFinal,
} from '@/tools/inventory';
import { AdvertisedPriceField, AdvertisedPriceStatus, IAdvertisedPriceFields } from '@/types/inventory';
import { RichText } from '@lamesarv-sdk/components/dist/RichText';
import { IBasicInventory, InventoryField } from '@lamesarv-sdk/types';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';

dayjs.extend(utc);
dayjs.extend(timezone);

export const formatPrice = (price: number) =>
  `$${Math.round(price).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

interface InventoryPricingProps {
  item: IBasicInventory & IAdvertisedPriceFields;
  className?: string;
  smaller?: boolean;
  nonRebateSmallPriceClass?: string;
  nonRebateClassName?: string;
  usePDP?: boolean;
  usePDPRebateVariant?: boolean;
  usePDPFeeInclusiveContent?: boolean;
  useStickyMode?: boolean;
}

export const InventoryPricing = ({
  item,
  className,
  nonRebateClassName,
  nonRebateSmallPriceClass,
  smaller,
  usePDP,
  usePDPFeeInclusiveContent,
  usePDPRebateVariant,
  useStickyMode,
}: InventoryPricingProps) => {
  const { env } = useEnv();
  const { context: layoutContext } = useContext(LayoutContext);

  // What to show and whether it is crossed out comes from the importer, see getAdvertisedPriceStatus
  const status = getAdvertisedPriceStatus(item);
  const advertisedPrice = item[AdvertisedPriceField.price];

  if (status === AdvertisedPriceStatus.noPrice || !advertisedPrice) {
    return <div className={twMerge('grow', usePDP && 'lg:min-h-[160px]', className)} />;
  }

  const isFinalPrice = isAdvertisedPriceFinal(item);
  const listPrice = item[InventoryField.priceList] || undefined;

  // Crossed-out MSRP only (MSRP selected in RVPoint)
  if (status === AdvertisedPriceStatus.crossedOut && advertisedPrice === listPrice) {
    return (
      <div
        className={twMerge(
          'grow flex gap-1.5 flex-col font-helvetica',
          usePDP && 'lg:min-h-[160px]',
          className,
          nonRebateClassName,
        )}
        data-testid="unit-msrp"
      >
        <span
          className="font-medium text-black-500 text-2xl sm:text-[1.75rem] line-through"
          data-testid="unit-showing-price"
        >
          {formatPrice(advertisedPrice)}
        </span>
      </div>
    );
  }

  const bigPrice = {
    label: status === AdvertisedPriceStatus.ok ? 'Price' : 'Sale Price',
    value: advertisedPrice,
  };
  // A final price never prints a lower MSRP/Was next to it (the fees can push it above MSRP)
  const smallPrice = !!listPrice &&
    (isFinalPrice ? listPrice > advertisedPrice : listPrice !== advertisedPrice) && {
      label: getListPriceLabel(item),
      value: listPrice,
    };

  const hasSavings = !!smallPrice && smallPrice.value > bigPrice.value;

  const feeInclusiveContentEnable = usePDPFeeInclusiveContent
    ? env.ENABLE_FEE_INCLUSIVE_CONTENT_PDP
    : env.ENABLE_FEE_INCLUSIVE_CONTENT_CARDS;
  const feeInclusiveContentCms = usePDPFeeInclusiveContent
    ? layoutContext?.detailsSettings?.feeInclusiveContentPdp
    : layoutContext?.detailsSettings?.feeInclusiveContentCards;
  const feeInclusiveContentDefault = usePDPFeeInclusiveContent
    ? FEE_INCLUSIVE_CONTENT_PDP_DEFAULT
    : FEE_INCLUSIVE_CONTENT_CARDS_DEFAULT;
  const feeInclusiveContentDisplay = feeInclusiveContentEnable && status === AdvertisedPriceStatus.ok;

  if (hasActiveRebate(item[InventoryField.rebate])) {
    return (
      <div
        className={twMerge(
          'grow flex flex-col font-helvetica',
          usePDP && 'lg:min-h-[160px]',
          usePDPRebateVariant ? 'gap-1 md:gap-2.5 lg:gap-1 text-base md:text-lg lg:text-base' : 'gap-0.5 text-base',
          className,
          useStickyMode && feeInclusiveContentDisplay && 'pb-8',
        )}
      >
        {smallPrice && (
          <div className="flex justify-between gap-4">
            <span>{smallPrice.label}</span>
            <span>{formatPrice(smallPrice.value)}</span>
          </div>
        )}
        {hasSavings && (
          <div className="flex justify-between gap-4">
            <span>Discount</span>
            <span className="text-green-600">- {formatPrice(smallPrice.value - bigPrice.value)}</span>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <span>{bigPrice.label}</span>
          <span>{formatPrice(bigPrice.value)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>Manufacturer Rebate</span>
          <span className="text-green-600">- {formatPrice(item[InventoryField.rebate].amount)}</span>
        </div>
        <div className={twMerge('flex justify-between gap-4 font-bold', usePDPRebateVariant ? 'text-xl' : 'text-lg')}>
          <span>Net Price</span>
          <span className={twMerge(!isFinalPrice && 'line-through')}>
            {formatPrice(bigPrice.value - item[InventoryField.rebate].amount)}
          </span>
        </div>
        <span className="text-xs text-black-350">
          Rebate ends on {dayjs.unix(item[InventoryField.rebate].enddate).tz('America/Phoenix').format('MMMM D, YYYY')}
        </span>
        {feeInclusiveContentDisplay && (
          <div className={twMerge('order-5', useStickyMode && 'absolute right-[21rem] bottom-0')}>
            {feeInclusiveContentCms ? (
              <RichText content={feeInclusiveContentCms} className="text-sm" />
            ) : (
              <span className="text-sm">{feeInclusiveContentDefault}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'flex gap-1.5 flex-col font-helvetica',
        usePDP && 'lg:min-h-[160px]',
        className,
        nonRebateClassName,
        useStickyMode && feeInclusiveContentDisplay && 'pb-8',
      )}
    >
      <div className="flex gap-3 text-2xl sm:text-[1.75rem] order-2">
        {!smaller && isFinalPrice && hasSavings && (
          <span className="text-red-500" data-testid="unit-discount-percentage">
            -{Math.floor(((smallPrice.value - bigPrice.value) / smallPrice.value) * 100)}%
          </span>
        )}
        <span
          className={twMerge(
            'text-black-500',
            !isFinalPrice && 'line-through',
            feeInclusiveContentDisplay ? 'font-bold' : 'font-medium',
          )}
          data-testid="unit-showing-price"
        >
          {formatPrice(bigPrice.value)}
        </span>
      </div>

      {smallPrice && (
        <span
          className={twMerge(
            'text-xs sm:text-sm text-black-350',
            smaller ? 'order-1' : 'order-3',
            nonRebateSmallPriceClass,
          )}
          data-testid="unit-was-price-label"
        >
          {smallPrice.label}{' '}
          <span className="line-through" data-testid="unit-was-price-value">
            {formatPrice(smallPrice.value)}
          </span>
        </span>
      )}

      {isFinalPrice && hasSavings && (
        <span className="font-bold text-xs sm:text-sm text-green-500 order-4" data-testid="unit-you-save">
          You save {formatPrice(smallPrice.value - bigPrice.value)}
        </span>
      )}

      {feeInclusiveContentDisplay && (
        <div className={twMerge('order-5', useStickyMode && 'absolute right-[21rem] bottom-0')}>
          {feeInclusiveContentCms ? (
            <RichText content={feeInclusiveContentCms} className="text-sm" />
          ) : (
            <span className="text-sm ">{feeInclusiveContentDefault}</span>
          )}
        </div>
      )}
    </div>
  );
};
